import React, { useEffect } from 'react'

import { Outlet, useLocation } from 'react-router-dom'

import Compose from '@components/Compose'
import APIErrorProvider from '@providers/APIErrorProvider'
import AudioContextProvider from '@providers/AudioContextProvider'
import BannerProvider, { useBanner } from '@providers/BannerProvider'
import PageApiProvider from '@providers/PageApiProvider'
import StripeProvider from '@providers/StripeProvider'
import { useDialogStore } from '@stores/dialog.store'
import { useGlobalDataStore } from '@stores/globalData/globalData.store'
import ErrorEmitter from '@utils/emitters/ErrorEmitter'
import GoogleTagManager from '@utils/gtm/google-tag-manager'
import i18n from '@utils/i18n'

import type { PropsWithChildren } from 'react'

const Root: React.FC = () => {
  return (
    <Compose
      components={[
        PageApiProvider,
        APIErrorProvider,
        AudioContextProvider,
        StripeProvider,
        BannerProvider,
      ]}>
      <AppWrapper>
        <Outlet />
      </AppWrapper>
    </Compose>
  )
}

export default Root

const AppWrapper: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const serverData = useGlobalDataStore((s) => s.serverData)
  const globalData = useGlobalDataStore((s) => s.globalData)

  const location = useLocation()
  const { showBanner } = useBanner()
  const closeAllDialogs = useDialogStore((s) => s.closeAllDialogs)

  useEffect(() => {
    let memberLevel = 'Guest'
    if (globalData.session?.isLoggedIn()) {
      memberLevel = globalData.session.current_user?.has_premium_features ? 'Premium' : 'Basic'
    }
    GoogleTagManager.updateUserProperties({ member_level: memberLevel })

    if (serverData?.login?.is_impersonating) {
      showBanner(`You are impersonating '${serverData.login.current_user?.username}'`, {
        label: 'click here to stop',
        action: () =>
          (window.location.href = `https://${serverData.login.community.hostname}/admin/end_impersonation`),
      })
    }
  }, [])

  useEffect(() => {
    ErrorEmitter.clearErrors()

    // Clear active dialogs when the route changes
    closeAllDialogs()
  }, [location])

  // Change language if the locale changes
  useEffect(() => {
    if (globalData.session) {
      const locale = globalData.session.locale ?? 'en'
      if (locale !== i18n.language) i18n.changeLanguage(locale).catch((err) => console.error(err))
    }
  }, [globalData.session])

  return <>{children}</>
}
